import { useTranslations } from 'next-intl';

import ResolvingLeftIcon from '@/assets/icons/account/resolving-left.svg';
import ResolvingRightIcon from '@/assets/icons/account/resolving-right.svg';
import CheckYellowIcon from '@/assets/icons/check_yellow.svg';
import OngoingIcon from '@/assets/icons/ongoing.svg';
import DollarIcon from '@/assets/icons/usdc.svg';
import { Image } from '@/components/shared/Image';
import { P } from '@/components/shared/Typography';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatTimestampToGMTString } from '@/lib/utils/formatTimestampToGMTString';
import { getVariantFromTokenSymbol } from '@/lib/utils/getVariantFromTokenSymbol';
import { T_PredictionWinnerModalType } from '@/ts/types/T_PredictionWinnerModalType';
import { T_VariantType } from '@/ts/types/T_VariantType';

import { modalVariants } from '../staticStyles';

interface I_ProfilePredictionDialogCardProps {
  variant: T_VariantType;
  type?: T_PredictionWinnerModalType;
  realPrice: string;
  selectedParticipation?: {
    predictionEvent: {
      tokenSymbol: string;
      shortName?: string;
      endTime: string;
      resolutionTime?: string;
      realPriceDecimal: string | null;
      tokenIcon?: {
        url: string;
        alt: string;
      };
    };
  } | null;
  decimals?: number;
}

export const ProfilePredictionDialogCard = ({
  variant,
  type,
  selectedParticipation,
  decimals = 2,
  realPrice
}: I_ProfilePredictionDialogCardProps) => {
  const t = useTranslations();

  const isOngoing = type === 'ongoing';
  const isResolving = type === 'resolving';

  const shortName =
    selectedParticipation?.predictionEvent?.shortName ||
    selectedParticipation?.predictionEvent?.tokenSymbol;

  const resolutionTime =
    selectedParticipation?.predictionEvent.resolutionTime || '';
  const endTime = selectedParticipation?.predictionEvent.endTime || '';

  const eventTitle = selectedParticipation
    ? isOngoing || isResolving
      ? `${t('profile.predictionWinners.resolution')}: ${formatTimestampToGMTString(resolutionTime || endTime)}`
      : `${shortName} Price on ${formatTimestampToGMTString(resolutionTime || endTime)}`
    : '';

  const actualPrice = realPrice
    ? `$ ${formatNumberWithCommasAndDecimals(realPrice, decimals)}`
    : '—';

  const validationInfo = t('profile.predictionWinners.validationInfo');
  const validVariant = getVariantFromTokenSymbol(
    selectedParticipation,
    variant
  );

  return (
    <Card
      className={cn(
        'relative mb-4 rounded-2xl overflow-hidden border-0',
        'banner-gradient-animation',
        modalVariants[validVariant].bgGradient
      )}
    >
      {selectedParticipation?.predictionEvent?.tokenIcon?.url ? (
        <div className="absolute right-0 top-0 w-24 h-24 opacity-10 pointer-events-none">
          <Image
            src={selectedParticipation.predictionEvent.tokenIcon.url}
            alt={selectedParticipation.predictionEvent.tokenIcon.alt || ''}
            width={96}
            height={96}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div
          className={cn(
            'absolute right-0 top-0 w-24 h-24 opacity-10 pointer-events-none bg-contain bg-no-repeat bg-center',
            modalVariants[validVariant].bgImage
          )}
        />
      )}

      <CardContent className="px-6 py-4 relative ">
        <div className="flex flex-col items-start gap-1 relative z-10">
          {isOngoing ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 relative overflow-hidden flex-shrink-0">
                  <OngoingIcon />
                </div>
                <P className="text-foreground text-base font-bold leading-6 uppercase">
                  {t('profile.transferHistory.statuses.ongoing')}
                </P>
              </div>
              <P className="text-foreground text-xs font-light leading-4">
                {eventTitle}
              </P>
            </>
          ) : isResolving ? (
            <>
              <div className="flex items-center gap-2">
                <ResolvingLeftIcon />
                <P className="text-foreground text-base font-bold leading-6 uppercase animate-pulse">
                  {t('profile.transferHistory.statuses.resolving')}
                </P>
                <ResolvingRightIcon />
              </div>
              <P className="text-foreground text-xs font-light leading-4">
                {eventTitle}
              </P>
            </>
          ) : (
            <>
              <P className="text-foreground text-xs font-light leading-4">
                {eventTitle}
              </P>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 relative overflow-hidden flex-shrink-0">
                  <DollarIcon />
                </div>

                <P className="text-foreground text-base font-bold leading-6">
                  {actualPrice}
                </P>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 relative overflow-hidden flex-shrink-0">
                  <CheckYellowIcon />
                </div>

                <P className="text-foreground text-xs font-bold leading-4">
                  {validationInfo}
                </P>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
